import { normalizeCartLines, type BusinessMode } from '@shopgnd/shared';
import type {
  AdminUserDetail,
  AdSlot,
  Category,
  Classified,
  ClassifiedStatus,
  OrderStatus,
  Product,
  Review,
  ServiceProvider,
  ServiceRequestStatus,
  SponsoredListing,
  Transaction,
  User,
  UserRole,
  Vendor,
} from '@shopgnd/shared';
import {
  createServiceProRecord,
  createVendorRecord,
  deleteProduct,
  deleteServiceProRecord,
  deleteVendorRecord,
  getServiceProvider,
  getVendor,
  listAllClassifieds,
  listAllProducts,
  listProductsByVendor,
  listServiceProviders,
  listVendorsStrict as listVendorsLive,
  updateClassifiedStatus,
  updateProduct,
  updateServiceProRecord,
  updateVendorRecord,
} from '@/api/catalog';
import {
  createCategory,
  deleteCategory,
  listCategories,
  updateCategory,
} from '@/api/categories';
import { getDataClient } from '@/lib/amplify-client';
import { isRealEstateVendor, propertyListingApprovalForAdminProvision } from '@/lib/realEstateVendor';
import { notifyVendorProfileChanged } from '@/lib/vendorProfileRefresh';
import { updateOrderStatus } from '@/api/orders';
import { fetchAdminOrdersSample } from '@/api/adminOrders';
import { adminSetServiceRequestStatus } from '@/api/serviceRequests';
import {
  createUserLive,
  getUserDetailLive,
  listUsersLive,
  updateUserProfileLive,
  provisionProLive,
  provisionVendorLive,
  setPlatformAdminLive,
  suspendUserLive,
  enableUserLive,
  deleteUserLive,
  updateUserRolesLive,
  type ProvisionOwnerFields,
  type ProvisionProFields,
  type ProvisionVendorFields,
} from '@/api/users';
import {
  notifyVendorApplicationDecided,
  type VendorApprovalDecisionEmailResult,
} from '@/api/vendorNotify';
import {
  expireSponsoredListingLive,
  listAdSlotsLive,
  listSponsoredListingsLive,
  listTransactionsLive,
  updateAdSlotLive,
} from '@/api/finance';
import { upsertVendorPayout, listVendorPayoutsAdmin } from '@/api/vendorPayout';
import { deleteReviewLive, listAllReviewsLive } from '@/api/reviews';
import { notifyCatalogRefresh } from '@/lib/catalog-refresh';
import { requestVendorIndexing } from '@/api/seoIndex';

export { fetchAdminOrdersPage, fetchAdminOrdersSample } from '@/api/adminOrders';

export type AdminAuditEntry = {
  id: string;
  action: string;
  targetType: string;
  targetId: string;
  detail?: string;
  adminUserId: string;
  createdAt: string;
};

const auditLog: AdminAuditEntry[] = [];

export function logAdminAction(
  adminUserId: string,
  action: string,
  targetType: string,
  targetId: string,
  detail?: string,
) {
  const entry: AdminAuditEntry = {
    id: `audit-${Date.now().toString(36)}`,
    action,
    targetType,
    targetId,
    detail,
    adminUserId,
    createdAt: new Date().toISOString(),
  };
  auditLog.unshift(entry);

  void (async () => {
    try {
      const client = await getDataClient();
      await client.models.AdminAuditLog.create({
        action,
        targetType,
        targetId,
        detail,
        adminUserId,
      });
    } catch (e) {
      console.warn('[ShopGND] Audit log persist failed', e);
    }
  })();
}

export async function listAdminAuditLog(max = 100): Promise<AdminAuditEntry[]> {
  await delay();
  try {
    const client = await getDataClient();
    const res = await client.models.AdminAuditLog.list();
    return ((res.data ?? []) as Record<string, unknown>[])
      .map((row) => ({
        id: String(row.id),
        action: String(row.action),
        targetType: String(row.targetType),
        targetId: String(row.targetId),
        detail: row.detail ? String(row.detail) : undefined,
        adminUserId: String(row.adminUserId),
        createdAt: String(row.createdAt ?? new Date().toISOString()),
      }))
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
      .slice(0, max);
  } catch (e) {
    console.error(e);
    return auditLog.slice(0, max);
  }
}

export async function getAdminStatsExtended() {
  const orders = await fetchAdminOrdersSample(100);
  const { listAllServiceRequests } = await import('@/api/serviceRequests');
  const requests = await listAllServiceRequests();
  const [vendors, products, classifieds, users, pros, transactions, sponsored, payouts] =
    await Promise.all([
      listVendors(),
      listAllProductsAdmin(),
      listAllClassifiedsAdmin(),
      listUsers(),
      listServicePros(),
      listTransactions(),
      listSponsoredListingsAdmin(),
      listVendorPayoutsAdmin(),
    ]);

  const revenueCents = orders
    .filter((o) => !['cancelled', 'pending_payment'].includes(o.status))
    .reduce((s, o) => s + o.totalCents, 0);

  return {
    flagged: classifieds.filter((c) => c.status === 'flagged').length,
    vendors: vendors.length,
    products: products.length,
    activeListings: classifieds.filter((c) => c.status === 'active').length,
    pendingOrders: orders.filter((o) =>
      ['paid', 'processing', 'pending_payment'].includes(o.status),
    ).length,
    orderCount: orders.length,
    revenueCents,
    openRequests: requests.filter((r) => ['open', 'accepted', 'in_progress'].includes(r.status))
      .length,
    pendingPayouts: payouts.filter((p) => !p.payoutsEnabled || !p.payoutAccountNumber).length,
    failedTxns: transactions.filter((t) => t.status === 'failed').length,
    activeBoosts: sponsored.filter((s) => new Date(s.endsAt) > new Date()).length,
    users: users.length,
    servicePros: pros.length,
  };
}

export async function getRecentOrdersAdmin(max = 5) {
  const orders = await fetchAdminOrdersSample(Math.max(max, 20));
  return [...orders].sort((a, b) => b.createdAt.localeCompare(a.createdAt)).slice(0, max);
}

// Users
export async function listUsers(): Promise<User[]> {
  await delay();
  return listUsersLive();
}

export async function getUserDetail(userId: string): Promise<AdminUserDetail> {
  return getUserDetailLive(userId);
}

export async function updateUserProfile(
  adminUserId: string,
  userId: string,
  patch: { displayName?: string; phone?: string; parish?: string },
): Promise<AdminUserDetail> {
  const user = await updateUserProfileLive(userId, patch);
  const parts = [
    patch.displayName ? `name=${patch.displayName}` : null,
    patch.phone !== undefined ? 'phone' : null,
    patch.parish !== undefined ? `parish=${patch.parish || 'cleared'}` : null,
  ].filter(Boolean);
  logAdminAction(adminUserId, 'update_profile', 'user', userId, parts.join(', ') || undefined);
  return user;
}

export async function listAdminAuditForUser(userId: string, max = 50): Promise<AdminAuditEntry[]> {
  try {
    const client = await getDataClient();
    const res = await client.models.AdminAuditLog.list({
      filter: {
        and: [{ targetType: { eq: 'user' } }, { targetId: { eq: userId } }],
      },
      limit: max,
    });
    return ((res.data ?? []) as Record<string, unknown>[])
      .map((row) => ({
        id: String(row.id),
        action: String(row.action),
        targetType: String(row.targetType),
        targetId: String(row.targetId),
        detail: row.detail ? String(row.detail) : undefined,
        adminUserId: String(row.adminUserId),
        createdAt: String(row.createdAt ?? new Date().toISOString()),
      }))
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  } catch (e) {
    console.error('[ShopGND] listAdminAuditForUser failed', e);
    return auditLog
      .filter((e) => e.targetType === 'user' && e.targetId === userId)
      .slice(0, max);
  }
}

export async function grantPlatformAdmin(
  adminUserId: string,
  targetUserId: string,
  grant: boolean,
): Promise<User | null> {
  const user = await setPlatformAdminLive(targetUserId, grant);
  logAdminAction(
    adminUserId,
    grant ? 'grant_platform_admin' : 'revoke_platform_admin',
    'user',
    targetUserId,
  );
  return user;
}

export async function updateUserRoles(
  adminUserId: string,
  userId: string,
  roles: UserRole[],
): Promise<User | null> {
  await delay();
  const unique = [...new Set(roles)];
  if (unique.length && !unique.includes('buyer')) unique.unshift('buyer');
  const nextRoles = unique.length ? unique : (['buyer'] as UserRole[]);

  const user = await updateUserRolesLive(userId, nextRoles);
  logAdminAction(adminUserId, 'update_roles', 'user', userId, nextRoles.join(', '));
  return user;
}

export async function suspendUser(adminUserId: string, userId: string): Promise<User> {
  await delay();
  const user = await suspendUserLive(userId);
  logAdminAction(adminUserId, 'suspend', 'user', userId);
  return user;
}

export async function enableUser(
  adminUserId: string,
  userId: string,
  roles?: UserRole[],
): Promise<User> {
  await delay();
  const user = await enableUserLive(userId, roles);
  logAdminAction(adminUserId, 'enable', 'user', userId);
  return user;
}

export async function deleteUser(adminUserId: string, userId: string): Promise<void> {
  await delay();
  await deleteUserLive(userId);
  logAdminAction(adminUserId, 'delete', 'user', userId);
}

export type { ProvisionOwnerFields, ProvisionProFields, ProvisionVendorFields };

export async function createUserAdmin(
  adminUserId: string,
  params: {
    owner: ProvisionOwnerFields;
    roles?: UserRole[];
    sendWelcomeEmail?: boolean;
  },
) {
  await delay();
  const result = await createUserLive(params);
  logAdminAction(adminUserId, 'create', 'user', result.userId, result.email);
  return result;
}

export async function provisionVendorAdmin(
  adminUserId: string,
  params: {
    owner: ProvisionOwnerFields;
    vendor: ProvisionVendorFields;
    sendWelcomeEmail?: boolean;
  },
) {
  await delay();
  const result = await provisionVendorLive(params);
  logAdminAction(adminUserId, 'create', 'vendor', result.vendorId, result.email);
  return result;
}

export async function provisionProAdmin(
  adminUserId: string,
  params: {
    owner: ProvisionOwnerFields;
    pro: ProvisionProFields;
    sendWelcomeEmail?: boolean;
  },
) {
  await delay();
  const result = await provisionProLive(params);
  logAdminAction(adminUserId, 'create', 'service_provider', result.proId, result.email);
  return result;
}

// Vendors
export type VendorDraft = {
  businessName: string;
  businessOwnerName?: string;
  /** Public /business/{slug} — changing slug records a SlugRedirect on save. */
  slug?: string;
  ownerUserId: string;
  description?: string;
  logoUrl?: string;
  acceptsCardPayments?: boolean;
  onlineOnly?: boolean;
  offersPickup?: boolean;
  offersDelivery?: boolean;
  pickupAddress?: string;
  pickupParish?: string;
  payoutBankName?: string;
  payoutAccountHolderName?: string;
  payoutAccountNumber?: string;
  payoutBranchCode?: string;
  payoutsEnabled: boolean;
  approvalStatus?: Vendor['approvalStatus'];
  approvalNote?: string;
  giftCertApprovalStatus?: Vendor['giftCertApprovalStatus'];
  giftCertApprovalNote?: string;
  cardPaymentApprovalStatus?: Vendor['cardPaymentApprovalStatus'];
  cardPaymentApprovalNote?: string;
  propertyListingApprovalStatus?: Vendor['propertyListingApprovalStatus'];
  propertyListingApprovalNote?: string;
  businessModes?: BusinessMode[];
  primaryMode?: Vendor['primaryMode'];
  businessType?: string;
  directorySector?: Vendor['directorySector'];
  directoryCategoryId?: string;
  directoryCategoryCustomLabel?: string;
  trustedPartner?: boolean;
  partnerBusinessIds?: string[];
};

export async function listVendors(): Promise<Vendor[]> {
  await delay();
  return listVendorsLive();
}

export async function getVendorWithStats(vendorId: string) {
  const vendor = await getVendor(vendorId);
  if (!vendor) return null;
  const orders = (await fetchAdminOrdersSample(200)).filter((o) => o.vendorId === vendorId);
  const products = await listProductsByVendor(vendorId);
  return {
    vendor,
    productCount: products.length,
    orderCount: orders.length,
  };
}

export async function createVendor(adminUserId: string | null, draft: VendorDraft): Promise<Vendor> {
  const { payoutBankName, payoutAccountHolderName, payoutAccountNumber, payoutBranchCode, payoutsEnabled, ...vendorDraft } = draft;
  const propertyAutoApprove = propertyListingApprovalForAdminProvision({
    businessType: vendorDraft.businessType,
    businessModes: vendorDraft.businessModes,
  });
  const vendor = await createVendorRecord({
    ...vendorDraft,
    approvalStatus: adminUserId ? (vendorDraft.approvalStatus ?? 'approved') : vendorDraft.approvalStatus,
    ...(propertyAutoApprove
      ? { propertyListingApprovalStatus: propertyAutoApprove }
      : {}),
  });
  if (
    payoutBankName ||
    payoutAccountHolderName ||
    payoutAccountNumber ||
    payoutsEnabled
  ) {
    await upsertVendorPayout(vendor.id, vendor.ownerUserId, {
      payoutBankName,
      payoutAccountHolderName,
      payoutAccountNumber,
      payoutBranchCode,
      payoutsEnabled,
    });
  }
  if (adminUserId) {
    logAdminAction(adminUserId, 'create', 'vendor', vendor.id, vendor.businessName);
  }
  if ((vendor.approvalStatus ?? 'none') === 'approved' && vendor.slug) {
    void requestVendorIndexing(vendor.id);
  }
  return vendor;
}

export type SetVendorFeatureApprovalResult = {
  vendor: Vendor;
  emailNotice: VendorApprovalDecisionEmailResult;
};

/** Approve or reject a vendor storefront and email the owner. */
export async function setVendorApprovalStatus(
  adminUserId: string,
  vendorId: string,
  status: 'approved' | 'rejected',
  note?: string,
): Promise<SetVendorFeatureApprovalResult | null> {
  return setVendorFeatureApprovalStatus(adminUserId, vendorId, 'store', status, note);
}

/** Approve or reject card payments / gift certificates and email the vendor owner. */
export async function setVendorFeatureApprovalStatus(
  adminUserId: string,
  vendorId: string,
  feature: 'store' | 'cardPayments' | 'giftCertificates' | 'propertyListings',
  status: 'approved' | 'rejected',
  note?: string,
): Promise<SetVendorFeatureApprovalResult | null> {
  let patch: Partial<VendorDraft> =
    feature === 'store'
      ? { approvalStatus: status, approvalNote: note }
      : feature === 'giftCertificates'
        ? { giftCertApprovalStatus: status, giftCertApprovalNote: note || undefined }
        : feature === 'propertyListings'
          ? { propertyListingApprovalStatus: status, propertyListingApprovalNote: note || undefined }
          : {
              cardPaymentApprovalStatus: status,
              cardPaymentApprovalNote: note || undefined,
              ...(status === 'rejected' ? { acceptsCardPayments: false } : {}),
            };

  if (feature === 'store' && status === 'approved') {
    const existing = await getVendor(vendorId);
    if (existing && isRealEstateVendor(existing)) {
      const propertyStatus = existing.propertyListingApprovalStatus ?? 'none';
      if (propertyStatus !== 'approved' && propertyStatus !== 'rejected') {
        patch = { ...patch, propertyListingApprovalStatus: 'approved' };
      }
    }
  }

  const updated = await updateVendorAdmin(adminUserId, vendorId, patch);
  if (!updated) return null;
  notifyVendorProfileChanged();

  const emailNotice = await notifyVendorApplicationDecided({
    vendorId,
    status,
    note,
    decisionType: feature,
  });
  if (emailNotice.kind === 'failed') {
    console.error('[ShopGND] Vendor approval email failed', emailNotice.message);
  }
  return { vendor: updated, emailNotice };
}

export async function updateVendorAdmin(
  adminUserId: string,
  vendorId: string,
  patch: Partial<VendorDraft>,
): Promise<Vendor | null> {
  await delay();
  const { payoutBankName, payoutAccountHolderName, payoutAccountNumber, payoutBranchCode, payoutsEnabled, ...vendorPatch } = patch;
  const updated = await updateVendorRecord(vendorId, vendorPatch);
  if (
    updated &&
    (payoutBankName !== undefined ||
      payoutAccountHolderName !== undefined ||
      payoutAccountNumber !== undefined ||
      payoutBranchCode !== undefined ||
      payoutsEnabled !== undefined)
  ) {
    await upsertVendorPayout(updated.id, updated.ownerUserId, {
      payoutBankName,
      payoutAccountHolderName,
      payoutAccountNumber,
      payoutBranchCode,
      payoutsEnabled,
    });
  }
  if (updated) {
    logAdminAction(adminUserId, 'update', 'vendor', vendorId, updated.businessName);
  }
  return updated;
}

export async function deleteVendor(adminUserId: string, vendorId: string): Promise<void> {
  await delay();
  const result = await deleteVendorRecord(vendorId);
  if (!result.ok) {
    throw new Error(result.error);
  }
  logAdminAction(adminUserId, 'delete', 'vendor', vendorId);
  notifyCatalogRefresh();
}

// Service pros
export type ProDraft = {
  ownerUserId: string;
  headline: string;
  bio?: string;
  categories: string[];
  parish: string;
  hourlyRateCents?: number;
  avatarUrl?: string;
};

export async function listServicePros(): Promise<ServiceProvider[]> {
  await delay();
  return listServiceProviders();
}

export async function getServicePro(id: string): Promise<ServiceProvider | undefined> {
  await delay();
  return getServiceProvider(id);
}

export async function createServicePro(adminUserId: string, draft: ProDraft): Promise<ServiceProvider> {
  await delay();
  const pro = await createServiceProRecord(draft);
  logAdminAction(adminUserId, 'create', 'service_provider', pro.id);
  return pro;
}

export async function updateServicePro(
  adminUserId: string,
  proId: string,
  patch: Partial<ProDraft>,
): Promise<ServiceProvider | null> {
  await delay();
  const updated = await updateServiceProRecord(proId, patch);
  if (updated) logAdminAction(adminUserId, 'update', 'service_provider', proId);
  return updated;
}

export async function deleteServicePro(adminUserId: string, proId: string): Promise<boolean> {
  await delay();
  const ok = await deleteServiceProRecord(proId);
  if (ok) logAdminAction(adminUserId, 'delete', 'service_provider', proId);
  return ok;
}

export async function adminUpdateServiceRequestStatus(
  adminUserId: string,
  id: string,
  status: ServiceRequestStatus,
) {
  const updated = await adminSetServiceRequestStatus(id, status);
  if (updated) logAdminAction(adminUserId, 'status_change', 'service_request', id, status);
  return updated;
}

// Orders
export async function adminUpdateOrder(
  adminUserId: string,
  orderId: string,
  status: OrderStatus,
  opts?: { trackingNumber?: string },
) {
  const updated = await updateOrderStatus(orderId, status, opts);
  if (updated) logAdminAction(adminUserId, 'status_change', 'order', orderId, status);
  return updated;
}

// Products
export async function listAllProductsAdmin(): Promise<Product[]> {
  await delay();
  return listAllProducts();
}

export async function deleteProductAdmin(adminUserId: string, productId: string): Promise<void> {
  await delay();
  await deleteProduct(productId);
  logAdminAction(adminUserId, 'delete', 'product', productId);
}

export async function setProductSponsoredRank(
  adminUserId: string,
  productId: string,
  sponsoredRank: number | undefined,
): Promise<Product | null> {
  await delay();
  const updated = await updateProduct(productId, { sponsoredRank });
  if (updated) logAdminAction(adminUserId, 'sponsor', 'product', productId, String(sponsoredRank ?? 'none'));
  return updated;
}

export async function approveProductAdmin(adminUserId: string, productId: string): Promise<Product | null> {
  await delay();
  const { updateProductListingAdmin } = await import('@/api/catalog');
  const updated = await updateProductListingAdmin(productId, { listingStatus: 'published', listingNote: undefined });
  if (updated) logAdminAction(adminUserId, 'approve', 'product', productId);
  return updated;
}

export async function rejectProductAdmin(
  adminUserId: string,
  productId: string,
  reason: string,
): Promise<Product | null> {
  await delay();
  const { updateProductListingAdmin } = await import('@/api/catalog');
  const updated = await updateProductListingAdmin(productId, {
    listingStatus: 'rejected',
    listingNote: reason.trim(),
  });
  if (updated) {
    logAdminAction(adminUserId, 'reject', 'product', productId, reason.trim());
    // TODO: notify vendor when notification pipeline exists
  }
  return updated;
}

export async function bulkUpdateProductsAdmin(
  adminUserId: string,
  productIds: string[],
  patch: Partial<import('@/api/catalog').ProductDraft>,
): Promise<number> {
  await delay();
  const { updateProductListingAdmin } = await import('@/api/catalog');
  let ok = 0;
  for (const id of productIds) {
    const updated = await updateProductListingAdmin(id, patch);
    if (updated) {
      ok++;
      logAdminAction(adminUserId, 'bulk_update', 'product', id, patch.listingStatus ?? 'patch');
    }
  }
  return ok;
}

// Classifieds
export async function listAllClassifiedsAdmin(): Promise<Classified[]> {
  await delay();
  return listAllClassifieds();
}

/** Property agency listings (Classified.category === property). */
export async function listAllPropertiesAdmin(): Promise<Classified[]> {
  const all = await listAllClassifiedsAdmin();
  return all.filter((c) => c.category === 'property');
}

export async function updateClassifiedStatusAdmin(
  adminUserId: string,
  id: string,
  status: ClassifiedStatus,
): Promise<Classified | null> {
  await delay();
  const updated = await updateClassifiedStatus(
    id,
    status,
    status === 'active' ? { reportCount: 0 } : undefined,
  );
  if (updated) logAdminAction(adminUserId, 'status_change', 'classified', id, status);
  return updated;
}

export async function listFlaggedClassifieds(): Promise<Classified[]> {
  await delay();
  const all = await listAllClassifieds();
  return all.filter((c) => c.status === 'flagged');
}

export async function moderateClassified(
  adminUserId: string,
  id: string,
  action: 'approve' | 'remove',
): Promise<Classified | null> {
  return updateClassifiedStatusAdmin(adminUserId, id, action === 'approve' ? 'active' : 'removed');
}

// Categories
export async function listCategoriesAdmin(): Promise<Category[]> {
  await delay();
  return listCategories();
}

export async function createCategoryAdmin(
  adminUserId: string,
  name: string,
  slug: string,
  parentId?: string,
): Promise<Category> {
  await delay();
  const { nextSortOrderForParent } = await import('@/lib/categoryTree');
  const all = await listCategories();
  const cat = await createCategory({
    name: name.trim(),
    slug: slug.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-'),
    parentId: parentId || undefined,
    sortOrder: nextSortOrderForParent(all, parentId || undefined),
  });
  logAdminAction(adminUserId, 'create', 'category', cat.id, cat.name);
  return cat;
}

export async function deleteCategoryAdmin(adminUserId: string, id: string): Promise<boolean> {
  await delay();
  const ok = await deleteCategory(id);
  if (ok) {
    logAdminAction(adminUserId, 'delete', 'category', id);
  }
  return ok;
}

export async function updateCategoryAdmin(
  adminUserId: string,
  id: string,
  patch: {
    name?: string;
    slug?: string;
    parentId?: string | null;
    imageUrl?: string | null;
    homeFeaturedRank?: number | null;
    shopNavRank?: number | null;
    sortOrder?: number | null;
  },
): Promise<Category | null> {
  await delay();
  const normalizedName = patch.name?.trim();
  const normalizedSlug = patch.slug
    ?.trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  const updated = await updateCategory(id, {
    ...(normalizedName ? { name: normalizedName } : {}),
    ...(normalizedSlug ? { slug: normalizedSlug } : {}),
    ...(patch.parentId !== undefined ? { parentId: patch.parentId || undefined } : {}),
    ...(patch.imageUrl !== undefined ? { imageUrl: patch.imageUrl || undefined } : {}),
    ...(patch.homeFeaturedRank !== undefined
      ? { homeFeaturedRank: patch.homeFeaturedRank ?? undefined }
      : {}),
    ...(patch.shopNavRank !== undefined ? { shopNavRank: patch.shopNavRank ?? undefined } : {}),
    ...(patch.sortOrder !== undefined ? { sortOrder: patch.sortOrder ?? undefined } : {}),
  });
  if (updated) {
    const detail =
      normalizedName ??
      normalizedSlug ??
      (patch.imageUrl !== undefined ? 'image updated' : undefined) ??
      (patch.homeFeaturedRank !== undefined ? `home rank ${patch.homeFeaturedRank ?? 'cleared'}` : undefined) ??
      (patch.shopNavRank !== undefined ? `nav rank ${patch.shopNavRank ?? 'cleared'}` : undefined) ??
      (patch.sortOrder !== undefined ? `sort order ${patch.sortOrder ?? 'cleared'}` : undefined);
    logAdminAction(adminUserId, 'update', 'category', id, detail);
  }
  return updated;
}

export async function setCategoryHomeFeaturedAdmin(
  adminUserId: string,
  categoryId: string,
  rank: 1 | 2 | 3 | null,
): Promise<Category | null> {
  await delay();
  const { canFeatureCategoryOnHome, MAX_HOME_FEATURED } = await import('@/lib/shopCategories');
  const all = await listCategories();
  const category = all.find((c) => c.id === categoryId);
  if (!category) return null;
  if (rank != null && !canFeatureCategoryOnHome(category)) {
    throw new Error('Only shop department categories can be featured on the home page.');
  }

  const featuredOthers = all.filter(
    (c) => c.homeFeaturedRank != null && c.id !== categoryId,
  );
  const alreadyFeatured = category.homeFeaturedRank != null;

  if (rank == null) {
    const updated = await updateCategory(categoryId, { homeFeaturedRank: undefined });
    if (updated) logAdminAction(adminUserId, 'update', 'category', categoryId, 'removed from home featured');
    return updated;
  }

  if (!alreadyFeatured && featuredOthers.length >= MAX_HOME_FEATURED) {
    throw new Error(`Maximum ${MAX_HOME_FEATURED} categories can be featured on the home page.`);
  }

  const conflict = all.find((c) => c.homeFeaturedRank === rank && c.id !== categoryId);
  if (conflict) {
    await updateCategory(conflict.id, { homeFeaturedRank: undefined });
  }

  const updated = await updateCategory(categoryId, { homeFeaturedRank: rank });
  if (updated) logAdminAction(adminUserId, 'update', 'category', categoryId, `home featured rank ${rank}`);
  return updated;
}

export async function reorderHomeFeaturedCategoriesAdmin(
  adminUserId: string,
  orderedCategoryIds: string[],
): Promise<Category[]> {
  await delay();
  const { canFeatureCategoryOnHome, MAX_HOME_FEATURED } = await import('@/lib/shopCategories');
  const ids = orderedCategoryIds.slice(0, MAX_HOME_FEATURED);
  const all = await listCategories();

  for (const id of ids) {
    const cat = all.find((c) => c.id === id);
    if (!cat || !canFeatureCategoryOnHome(cat)) {
      throw new Error('Invalid category in home featured reorder list.');
    }
  }

  const previouslyFeatured = all.filter((c) => c.homeFeaturedRank != null);
  for (const cat of previouslyFeatured) {
    if (!ids.includes(cat.id)) {
      await updateCategory(cat.id, { homeFeaturedRank: undefined });
    }
  }

  const updated: Category[] = [];
  for (let i = 0; i < ids.length; i++) {
    const cat = await updateCategory(ids[i], { homeFeaturedRank: i + 1 });
    if (cat) updated.push(cat);
  }

  logAdminAction(adminUserId, 'update', 'category', ids.join(','), 'reordered home featured');
  notifyCatalogRefresh();
  return updated;
}

export async function setCategoryShopNavAdmin(
  adminUserId: string,
  categoryId: string,
  rank: number | null,
): Promise<Category | null> {
  await delay();
  const { canFeatureCategoryInShopNav, MAX_SHOP_NAV } = await import('@/lib/shopCategories');
  const all = await listCategories();
  const category = all.find((c) => c.id === categoryId);
  if (!category) return null;
  if (rank != null && !canFeatureCategoryInShopNav(category)) {
    throw new Error('Only shop department categories can appear in shop navigation.');
  }

  const navOthers = all.filter((c) => c.shopNavRank != null && c.id !== categoryId);
  const alreadyNav = category.shopNavRank != null;

  if (rank == null) {
    const updated = await updateCategory(categoryId, { shopNavRank: undefined });
    if (updated) logAdminAction(adminUserId, 'update', 'category', categoryId, 'removed from shop nav');
    return updated;
  }

  if (!alreadyNav && navOthers.length >= MAX_SHOP_NAV) {
    throw new Error(`Maximum ${MAX_SHOP_NAV} categories can appear in shop navigation.`);
  }

  const conflict = all.find((c) => c.shopNavRank === rank && c.id !== categoryId);
  if (conflict) {
    await updateCategory(conflict.id, { shopNavRank: undefined });
  }

  const updated = await updateCategory(categoryId, { shopNavRank: rank });
  if (updated) logAdminAction(adminUserId, 'update', 'category', categoryId, `shop nav rank ${rank}`);
  notifyCatalogRefresh();
  return updated;
}

export async function reorderShopNavCategoriesAdmin(
  adminUserId: string,
  orderedCategoryIds: string[],
): Promise<Category[]> {
  await delay();
  const { canFeatureCategoryInShopNav, MAX_SHOP_NAV } = await import('@/lib/shopCategories');
  const ids = orderedCategoryIds.slice(0, MAX_SHOP_NAV);
  const all = await listCategories();

  for (const id of ids) {
    const cat = all.find((c) => c.id === id);
    if (!cat || !canFeatureCategoryInShopNav(cat)) {
      throw new Error('Invalid category in shop nav reorder list.');
    }
  }

  const previouslyNav = all.filter((c) => c.shopNavRank != null);
  for (const cat of previouslyNav) {
    if (!ids.includes(cat.id)) {
      await updateCategory(cat.id, { shopNavRank: undefined });
    }
  }

  const updated: Category[] = [];
  for (let i = 0; i < ids.length; i++) {
    const cat = await updateCategory(ids[i], { shopNavRank: i + 1 });
    if (cat) updated.push(cat);
  }

  logAdminAction(adminUserId, 'update', 'category', ids.join(','), 'reordered shop nav');
  notifyCatalogRefresh();
  return updated;
}

/** Reorder categories among siblings (same parent, or top-level when parentId is null). */
export async function reorderCategorySiblingsAdmin(
  adminUserId: string,
  parentId: string | null,
  orderedCategoryIds: string[],
): Promise<Category[]> {
  await delay();
  const { getSiblingCategories } = await import('@/lib/categoryTree');
  const all = await listCategories();
  const parentKey = parentId || undefined;
  const siblings = getSiblingCategories(all, parentKey);
  const siblingIds = new Set(siblings.map((c) => c.id));

  if (orderedCategoryIds.length !== siblings.length) {
    throw new Error('Reorder must include every category at this level.');
  }
  for (const id of orderedCategoryIds) {
    if (!siblingIds.has(id)) {
      throw new Error('Invalid category in reorder list.');
    }
  }

  const updated: Category[] = [];
  for (let i = 0; i < orderedCategoryIds.length; i++) {
    const cat = await updateCategory(orderedCategoryIds[i], { sortOrder: i + 1 });
    if (cat) updated.push(cat);
  }

  const parentLabel = parentKey
    ? all.find((c) => c.id === parentKey)?.name ?? parentKey
    : 'top level';
  logAdminAction(
    adminUserId,
    'update',
    'category',
    orderedCategoryIds.join(','),
    `reordered siblings under ${parentLabel}`,
  );
  notifyCatalogRefresh();
  return updated;
}

export async function importSeedCategoriesAdmin(_adminUserId: string): Promise<number> {
  return 0;
}

export async function resolveCategoryRequestAdminAction(
  adminUserId: string,
  requestId: string,
  action: import('@/api/categoryRequests').ResolveCategoryRequestAction,
): Promise<boolean> {
  await delay();
  const { resolveCategoryRequestAdmin } = await import('@/api/categoryRequests');
  const result = await resolveCategoryRequestAdmin(requestId, action);
  if (!result) return false;
  const label =
    action.type === 'approve'
      ? 'approved new category'
      : action.type === 'assign'
        ? 'assigned existing category'
        : 'rejected category request';
  logAdminAction(adminUserId, 'update', 'category_request', requestId, label);
  notifyCatalogRefresh();
  return true;
}

// Reviews
export async function listAllReviewsAdmin(): Promise<Review[]> {
  await delay();
  return listAllReviewsLive();
}

export async function deleteReviewAdmin(adminUserId: string, reviewId: string): Promise<boolean> {
  await delay();
  const ok = await deleteReviewLive(reviewId);
  if (ok) logAdminAction(adminUserId, 'delete', 'review', reviewId);
  return ok;
}

// Finance
export async function listTransactions(): Promise<Transaction[]> {
  await delay();
  return listTransactionsLive();
}

export async function listSponsoredListingsAdmin(): Promise<SponsoredListing[]> {
  await delay();
  return listSponsoredListingsLive();
}

export async function expireSponsoredListingAdmin(
  adminUserId: string,
  id: string,
): Promise<SponsoredListing | null> {
  await delay();
  const updated = await expireSponsoredListingLive(id);
  if (updated) logAdminAction(adminUserId, 'expire', 'sponsored_listing', id);
  return updated;
}

// Ad slots
export async function listAdSlots(): Promise<AdSlot[]> {
  await delay();
  return listAdSlotsLive();
}

export async function toggleAdSlot(adminUserId: string, id: string): Promise<AdSlot | null> {
  await delay();
  const slots = await listAdSlotsLive();
  const slot = slots.find((s) => s.id === id);
  if (!slot) return null;
  const updated = await updateAdSlotLive(id, { active: !slot.active });
  if (updated) {
    logAdminAction(adminUserId, 'toggle', 'ad_slot', id, updated.active ? 'active' : 'inactive');
  }
  return updated;
}

export type AdSlotDraft = {
  label: string;
  placement: AdSlot['placement'];
  basePriceCents: number;
  currency: string;
  active: boolean;
};

export async function updateAdSlotAdmin(
  adminUserId: string,
  id: string,
  patch: Partial<AdSlotDraft>,
): Promise<AdSlot | null> {
  await delay();
  const updated = await updateAdSlotLive(id, patch);
  if (updated) logAdminAction(adminUserId, 'update', 'ad_slot', id);
  return updated;
}

// Vendor stats (used by vendor overview — keep here for shared access)
export async function getVendorStats(vendorId: string) {
  const { listOrdersForVendor } = await import('@/api/orders');
  const [orders, products] = await Promise.all([
    listOrdersForVendor(vendorId),
    listProductsByVendor(vendorId),
  ]);
  const revenueCents = (orders ?? [])
    .filter((o) => !['cancelled', 'pending_payment'].includes(o.status))
    .reduce((sum, o) => sum + o.totalCents, 0);
  return {
    productCount: products.length,
    openOrders: (orders ?? []).filter((o) => ['paid', 'processing'].includes(o.status)).length,
    revenueCents,
    lowStock: products.filter((p) => p.stock > 0 && p.stock <= 5).length,
  };
}

export async function getVendorLowStock(vendorId: string, max = 5): Promise<Product[]> {
  const products = await listProductsByVendor(vendorId);
  return products.filter((p) => p.stock > 0 && p.stock <= 5).slice(0, max);
}

export async function getVendorRecentOrders(vendorId: string, max = 5) {
  const { listOrdersForVendor } = await import('@/api/orders');
  const orders = await listOrdersForVendor(vendorId);
  return [...(orders ?? [])]
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .slice(0, max);
}

export async function getVendorAnalytics(vendorId: string, days = 30) {
  const { listOrdersForVendor } = await import('@/api/orders');
  const [orders, products] = await Promise.all([
    listOrdersForVendor(vendorId),
    listProductsByVendor(vendorId),
  ]);

  const validOrders = (orders ?? []).filter(
    (o) => !['cancelled', 'pending_payment'].includes(o.status),
  );

  // Build daily GMV for requested date range
  const now = new Date();
  const gmvByDay: { date: string; cents: number }[] = [];
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(now);
    d.setDate(d.getDate() - i);
    gmvByDay.push({ date: d.toISOString().slice(0, 10), cents: 0 });
  }
  for (const o of validOrders) {
    const dateStr = o.createdAt.slice(0, 10);
    const entry = gmvByDay.find((e) => e.date === dateStr);
    if (entry) entry.cents += o.totalCents;
  }

  // Top products by revenue
  const revenueMap = new Map<string, { title: string; revenue: number; qty: number }>();
  for (const o of validOrders) {
    for (const line of normalizeCartLines(o.lines)) {
      const existing = revenueMap.get(line.productId) ?? {
        title: line.title,
        revenue: 0,
        qty: 0,
      };
      existing.revenue += line.priceCents * line.quantity;
      existing.qty += line.quantity;
      revenueMap.set(line.productId, existing);
    }
  }
  const topProducts = [...revenueMap.entries()]
    .map(([productId, d]) => ({ productId, ...d }))
    .sort((a, b) => b.revenue - a.revenue)
    .slice(0, 5);

  const aovCents =
    validOrders.length > 0
      ? Math.round(validOrders.reduce((s, o) => s + o.totalCents, 0) / validOrders.length)
      : 0;

  return {
    gmvByDay,
    topProducts,
    aovCents,
    orderCount: validOrders.length,
    productCount: products.length,
  };
}

export async function getAdminStats() {
  const s = await getAdminStatsExtended();
  return {
    flagged: s.flagged,
    vendors: s.vendors,
    products: s.products,
    activeListings: s.activeListings,
    pendingOrders: s.pendingOrders,
    orderCount: s.orderCount,
  };
}

function delay(ms = 80) {
  return new Promise((r) => setTimeout(r, ms));
}
